/**
 * IntlProvider onError handler — ONE path for every environment (dev == prod).
 *
 * Why a missing key at runtime is now rare, and never a page-blanking throw:
 *
 *   - The codegen parity gate (`tools/codegen/generators/i18n-keys.mjs`)
 *     guarantees every locale carries every key + matching ICU args, and
 *     `MessageKeys<N>` makes `t('badKey')` a COMPILE error.
 *   - Dynamic keys go through `t.dyn`, whose argument is typed `DynKey<N>` — a
 *     literal catalog key OR a codegen-derived template-literal PATTERN
 *     (`faqs.list.${number}.question`) — with a hand-typed single literal
 *     rejected (`NonLiteralDynKey`) and a plain `string` rejected by the
 *     `K extends DynKey<N>` bound. So a typo'd dynamic PREFIX or FIELD is also a
 *     compile error, at the call site.
 *
 * The residual RUNTIME miss surface is therefore narrow and cannot be closed at
 * compile time: an out-of-bounds numeric INDEX on a dynamic family
 * (`faqs.list.${n}` with `n` past the array end — unbounded by construction), or
 * a catalog edited outside the gate.
 *
 * For that residual we WARN + capture telemetry and let react-intl render the
 * raw key — in DEV and PROD alike. Previously this handler FORKED on the
 * environment (`isProduction()`): it threw in dev/test but only warned in prod.
 * That was a real dev/prod behavioural divergence the compiler could not see,
 * and it hid in prod the exact failure it was meant to catch. It is now one
 * path.
 *
 * We NEVER throw on the render path: a throw inside React's
 * `renderToReadableStream` blanks the whole page for the user, strictly worse
 * than one mis-rendered string. The ONLY loud signal is a thrown error under the
 * Vitest runner (`isTestRunner()`) — so a genuine escape turns a unit test red —
 * gated on the TEST-RUNNER env, never on `isProduction()` / `isDev()`.
 */
import { isTestRunner } from '@letsgethome/lib/env';
import { ReactIntlErrorCode } from 'react-intl';

import { clientLogger } from '../lib/client-logger';

type IntlError = { code: string; message: string };

export function intlOnError(err: IntlError): void {
  const label =
    err.code === ReactIntlErrorCode.MISSING_TRANSLATION
      ? `MISSING TRANSLATION: ${err.message}`
      : `${err.code}: ${err.message}`;
  const message = `[i18n] ${label}`;

  // Capture — same intent on both sides of the isomorphic boundary:
  //   server  → structured stdout (CloudWatch → Loki). `console.warn` keeps the
  //             heavy pino/OTel logger out of the browser bundle and never
  //             throws, so it is safe inside `renderToReadableStream`.
  //   browser → buffered telemetry beacon → BFF → Loki (`clientLogger`).
  if (typeof window === 'undefined') {
    console.warn(message);
  } else {
    clientLogger.warn({ component: 'intl', code: err.code }, message);
  }

  // The single loud signal: fail the Vitest run so an escaped key is caught in
  // CI. This is unreachable on the production/dev SSR render path.
  if (isTestRunner()) {
    throw new Error(message);
  }
}
